import { RichUtils, EditorState, EntityInstance, ContentBlock } from 'draft-js';

export type { Store } from './createStore';
export { createStore } from './createStore';

export interface DecodedOffset {
  blockKey: string;
  decoratorKey: number;
  leafKey: number;
}

export default {
  decodeOffsetKey(offsetKey: string): DecodedOffset {
    const [blockKey, decoratorKey, leafKey] = offsetKey.split('-');
    return {
      blockKey,
      decoratorKey: parseInt(decoratorKey, 10),
      leafKey: parseInt(leafKey, 10),
    };
  },

  createLinkAtSelection(editorState: EditorState, url: string): EditorState {
    const contentState = editorState.getCurrentContent().createEntity('LINK', 'MUTABLE', { url });
    const entityKey = contentState.getLastCreatedEntityKey();
    const withLink = RichUtils.toggleLink(editorState, editorState.getSelection(), entityKey);
    return EditorState.forceSelection(withLink, editorState.getSelection());
  },

  removeLinkAtSelection(editorState: EditorState): EditorState {
    const selection = editorState.getSelection();
    return RichUtils.toggleLink(editorState, selection, null);
  },

  collapseToEnd(editorState: EditorState): EditorState {
    const selection = editorState.getSelection();

    return EditorState.forceSelection(
      editorState,
      selection.merge({
        anchorKey: selection.getEndKey(),
        focusKey: selection.getEndKey(),
        anchorOffset: selection.getEndOffset(),
        focusOffset: selection.getEndOffset(),
      })
    );
  },

  getCurrentEntityKey(editorState: EditorState): string {
    const selection = editorState.getSelection();
    const anchorKey = selection.getAnchorKey();
    const contentState = editorState.getCurrentContent();
    const anchorBlock = contentState.getBlockForKey(anchorKey);
    const offset = selection.getAnchorOffset();
    let index = selection.getIsBackward() ? offset - 1 : offset;

    if (selection.getHasFocus() && anchorBlock && anchorBlock.getType() === 'atomic' && index > 0) {
      // `atomic` can be: Image or Button. `atomic` will have been wrapped by `figure` element in editor. So, the offset value of it always `0`
      // Fixed case: The CTA form didn't show when clicking on the last CTA button in registration editing page
      index = 0;
    }

    return anchorBlock && anchorBlock.getEntityAt(index);
  },

  getCurrentEntity(editorState?: EditorState): EntityInstance | null {
    if (!editorState) return null;
    const contentState = editorState.getCurrentContent();
    const entityKey = this.getCurrentEntityKey(editorState);
    return entityKey ? contentState.getEntity(entityKey) : null;
  },

  hasEntity(editorState: EditorState, entityType: string): boolean {
    const entity = this.getCurrentEntity(editorState);
    return Boolean(entity && entity.getType() === entityType);
  },
};

export const hasOrderedListItems = (contentBlocks: ContentBlock[]) => {
  return contentBlocks.filter((contentBlock) =>
    ['ordered-list-item', 'unordered-list-item'].includes(contentBlock.getType())
  ).length
    ? true
    : false;
};

export const picUrlPattern =
  /(?:^|[^"'])((http(s?):)\/\/((?:[a-z0-9\-]+\.)+[a-z|]{2,6}(?:\/[^\/#?]+)+\.(?:jpe?g|gif|png|svg)|(?:[a-z0-9\-]+\.imgix\.))(\?|\/?[^\s"'`#;,])*)/;
const hyperLinkRegex = /<a[\s]+[^>]*?href=[\\\"|'](?<href>.*?)[\\\"|']+[^>]*>([^<]+|.*?)?<\/a>/gi;
const imgRegex = /(<img.*?src=[\\\"|'](?<src>.*?)[\\\"|'].*?[\/]?>)/gi;
export const headingRegex = /<h\d{1}[^>]*>([^<]+|.*?)?<\/h\d{1}>/gi;

// TODO: use temp link to fix `unknow { field name } format in anchor link when pasting`
const tempLink = 'http://link.temp/';

const encodeDynamicUrl = (value: string) => {
  return tempLink + encodeURI(value);
};

/**
 * Convert default markdown text of notification email to markdown in draftjs
 * @param content
 * @returns
 */
export const standardizedMarkdownFormat = (content: string) => {
  // Some markdown have: \n\n\n\n or only \u200b\n
  // it not compatible with markdown format of stateFromMarkdown plugin
  // Multi spaces will be converted to single space
  return content
    .replace(/\n\n\n\n/g, '\n\n\u200b\n\n')
    .replace(/\u200b\n\n/g, '#emptyline#')
    .replace(/\u200b\n/g, '#emptyline#')
    .replace(/\#emptyline\#/g, '\u200b\n\n')
    .replace(/  +/g, ' ') // Multi spaces to single space
    .replace(/\n$/g, ''); // Remove '\n' at the end of markdown string
};

export const standardizedMarkdownContent = (content: string) => {
  return content.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, (_match: string, p1: string, p2: string) => {
    if (p2.indexOf(tempLink) !== -1) {
      p2 = decodeURI(p2.replace(tempLink, ''));
    }
    return `[${p1}](${p2})`;
  });
};

export const standardizedHtmlForPasting = (content: string): string => {
  const picUrlRegexForAll = new RegExp(picUrlPattern, 'gi');
  if (!content) return '';
  content = content
    .replace(/<br/g, '\u200b<br')
    .replace(/\n\n/gi, '<br/><br/>\u200b<br/><br/>\u200b<br/><br/>')
    .replace(/\n/gi, '<br/>')
    .replace(hyperLinkRegex, (_match: string, p1: string, p2: string) => {
      // TODO: encode URL with format: { field name }
      const url = decodeURI(p1);
      const matches = url.match(/{[a-zA-Z._\s]*}/gi);
      if (matches !== null && typeof matches !== 'undefined') {
        p1 = encodeDynamicUrl(matches[0]);
      }
      // TODO: change Hyperlink IMG to IMG
      const matchImg = imgRegex.exec(p2);
      if (matchImg !== null && typeof matchImg !== 'undefined') {
        return `<img src="${matchImg[2]}" url="${p1}" />`;
      }
      return `<a href="${p1}" target="_blank">${p2}</a>`;
    }) // picture url not in IMG or hyperlink to IMG
    .replace(picUrlRegexForAll, '<img src="$1" />') // convert to atomic images
    .replace(imgRegex, '<figure>$1</figure>');

  if (content.substring(0, 8) === '<figure>') {
    content = '\u200b' + content;
  }
  return content;
};
